import { loadStripe } from '@stripe/stripe-js'

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
const DEFAULT_FUNCTIONS_BASE_URL = '/.netlify/functions'

let stripePromise
const stripePromiseByAccount = {}

const getFunctionsBaseUrl = () =>
  import.meta.env.VITE_STRIPE_FUNCTIONS_BASE_URL || DEFAULT_FUNCTIONS_BASE_URL

const requestJson = async (endpoint, payload, options = {}) => {
  const response = await fetch(`${getFunctionsBaseUrl()}/${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    },
    body: JSON.stringify(payload ?? {})
  })

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}))
    const errorMessage = errorBody?.error || response.statusText
    throw new Error(errorMessage)
  }

  return response.json()
}

const getStripePublishableKey = () => {
  if (typeof STRIPE_PUBLISHABLE_KEY !== 'string') {
    return null
  }

  const trimmedKey = STRIPE_PUBLISHABLE_KEY.trim()

  return trimmedKey.length > 0 ? trimmedKey : null
}

export const getStripe = () => {
  const publishableKey = getStripePublishableKey()

  if (!publishableKey) {
    return Promise.resolve(null)
  }

  if (!stripePromise) {
    stripePromise = loadStripe(publishableKey)
  }

  return stripePromise
}

/**
 * For Direct Charges: load Stripe.js with platform key + stripeAccount so
 * Payment Element and confirmPayment target the connected account (avoids 404).
 * @param {string | null | undefined} stripeAccount - Connected account ID (acct_xxx), or null for platform-only
 * @returns {Promise<Stripe | null>}
 */
export const getStripeForConnect = (stripeAccount) => {
  const publishableKey = getStripePublishableKey()
  if (!publishableKey) return Promise.resolve(null)
  if (!stripeAccount) return getStripe()
  if (!stripePromiseByAccount[stripeAccount]) {
    stripePromiseByAccount[stripeAccount] = loadStripe(publishableKey, { stripeAccount })
  }
  return stripePromiseByAccount[stripeAccount]
}

export const createPaymentIntent = async ({
  amount,
  currency = 'usd',
  metadata,
  customerEmail,
  description,
  eventId,
  usePlatformAccount
}) =>
  requestJson('create-payment-intent', {
    amount,
    currency,
    metadata,
    customerEmail,
    description,
    eventId,
    usePlatformAccount
  })

export const createCheckoutSession = async ({
  lineItems,
  successUrl,
  cancelUrl,
  metadata,
  customerEmail
}) =>
  requestJson('create-checkout-session', {
    lineItems,
    successUrl,
    cancelUrl,
    metadata,
    customerEmail
  })

export const reportPaymentStatus = async ({
  paymentIntentId,
  status,
  errorMessage,
  metadata
}) =>
  requestJson('payment-status', {
    paymentIntentId,
    status,
    errorMessage,
    metadata
  })

/**
 * Create a Stripe Connect account link for the authenticated agent.
 * Pass the Supabase JWT (session.access_token) so the Netlify function can validate the user.
 * @param {string} accessToken - Supabase session access_token (JWT)
 * @returns {Promise<{ url: string }>} - Stripe Connect onboarding URL
 */
export const connectStripeAccount = async (accessToken) => {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new Error('Valid access token is required to connect Stripe account.')
  }
  const response = await fetch(`${getFunctionsBaseUrl()}/create-connect-account`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken.trim()}`
    },
    body: JSON.stringify({})
  })

  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    const errorMessage = body?.error || response.statusText || 'Failed to create Stripe Connect link'
    throw new Error(errorMessage)
  }
  return body
}
